// Müşteriler › İşletmeler — /clients/:id/entities (M16 T17, spec K16/C9, mockup 18).
// Başlık + sekme çubuğu (DR5) + özet şerit + ağaç tablosu (sol) + detay paneli (sağ).
// Tek şirketli müşteride tablo yalnız varsayılan işletmeyi gösterir ve "Grup yapısı ekle" CTA'sı
// çıkar. Değeri olan işletmeye alt işletme eklenirken 409 → "Değerleri taşı" diyaloğu (C9).

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { buildEntityTree, type Client, type ClientEntity } from "@mahir/shared";
import { api, ApiError } from "../api/client";
import {
  createClientEntity,
  deleteClientEntity,
  getEntityImpact,
  listClientEntities,
  moveEntityValues,
  reorderClientEntities,
  updateClientEntity,
  type EntityImpact,
} from "../api/client-entities";
import { ClientTabs } from "../components/clients/ClientTabs";
import { EntityDrawer, type EntityDraft } from "../components/clients/EntityDrawer";
import { EntityTree } from "../components/clients/EntityTree";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { EmptyState, ErrorState, LoadingState } from "../components/common/StateSurface";
import { useBreadcrumbDetail } from "../hooks/use-breadcrumb-detail";
import { parseEntityYearInput } from "../lib/entity-validity-ui";
import { useAuthStore } from "../stores/auth-store";

const EMPTY_DRAFT: EntityDraft = {
  name: "",
  type: "legal_entity",
  parentId: null,
  country: "",
  isActive: true,
  validFrom: "",
  validTo: "",
};

/** TODO-KPI-11 — çekmece metni → API (boş = null; geçersiz metin çekmecede zaten engellenir). */
const yearOrNull = (s: string): number | null => parseEntityYearInput(s) ?? null;

type Pending = { kind: "none" } | { kind: "create" | "edit"; id: string | null };

export function ClientEntities(): JSX.Element {
  const { id: clientId = "" } = useParams();
  const user = useAuthStore((s) => s.user);
  const canEdit =
    user?.permissions["clients.edit_all"] === true ||
    user?.permissions["clients.edit_own"] === true;

  const [client, setClient] = useState<Client | null>(null);
  const [entities, setEntities] = useState<ClientEntity[] | null>(null);
  // Finar rev-A 15 — breadcrumb "Müşteriler / <müşteri adı>" (Profil ile aynı kanca)
  useBreadcrumbDetail(client?.name);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>({ kind: "none" });
  const [draft, setDraft] = useState<EntityDraft>(EMPTY_DRAFT);
  const [impact, setImpact] = useState<EntityImpact | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [moveDialog, setMoveDialog] = useState<{ fromId: string; count: number } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [c, list] = await Promise.all([
        api<{ client: Client }>(`/clients/${clientId}`),
        listClientEntities(clientId),
      ]);
      setClient(c.client);
      setEntities(list);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "İşletmeler yüklenemedi");
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) void load();
  }, [clientId, load]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  const tree = useMemo(() => (entities ? buildEntityTree(entities) : null), [entities]);
  const groups = useMemo(
    () =>
      (entities ?? []).filter(
        (e) => e.type === "group" || (tree?.byId.get(e.id)?.children.length ?? 0) > 0,
      ),
    [entities, tree],
  );
  const activeLeaves = tree?.activeLeaves().length ?? 0;
  const passiveCount = (entities ?? []).filter((e) => !e.isActive).length;
  const selected =
    pending.kind === "edit" ? ((entities ?? []).find((e) => e.id === pending.id) ?? null) : null;
  const singleCompany = entities !== null && entities.length <= 1 && groups.length === 0;

  async function openEdit(id: string): Promise<void> {
    const e = (entities ?? []).find((x) => x.id === id);
    if (!e) return;
    setFormError(null);
    setPending({ kind: "edit", id });
    setDraft({
      name: e.name,
      type: e.type,
      parentId: e.parentId,
      country: e.country ?? "",
      isActive: e.isActive,
      validFrom: e.validFrom == null ? "" : String(e.validFrom),
      validTo: e.validTo == null ? "" : String(e.validTo),
    });
    setImpact(null);
    try {
      setImpact(await getEntityImpact(id));
    } catch {
      setImpact(null);
    }
  }

  function openCreate(type: "group" | "legal_entity"): void {
    setFormError(null);
    setImpact(null);
    setPending({ kind: "create", id: null });
    setDraft({ ...EMPTY_DRAFT, type, parentId: null });
  }

  async function save(d: EntityDraft): Promise<void> {
    setBusy(true);
    setFormError(null);
    try {
      if (pending.kind === "create") {
        await createClientEntity(clientId, {
          name: d.name.trim(),
          type: d.type,
          parentId: d.type === "group" ? null : d.parentId,
          country: d.country || null,
          validFrom: yearOrNull(d.validFrom),
          validTo: yearOrNull(d.validTo),
        });
        setFlash("İşletme eklendi");
      } else if (pending.kind === "edit" && pending.id) {
        await updateClientEntity(pending.id, {
          name: d.name.trim(),
          type: d.type,
          parentId: d.type === "group" ? null : d.parentId,
          country: d.country || null,
          isActive: d.isActive,
          validFrom: yearOrNull(d.validFrom),
          validTo: yearOrNull(d.validTo),
        });
        setFlash("Kaydedildi");
      }
      setPending({ kind: "none" });
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.code === "CLIENT_ENTITY_HAS_VALUES") {
        const details = (err.details ?? {}) as { valueCount?: number; entityId?: string };
        const parentId = d.parentId ?? details.entityId ?? null;
        if (parentId) {
          setMoveDialog({ fromId: parentId, count: details.valueCount ?? 0 });
          return;
        }
      }
      setFormError(err instanceof Error ? err.message : "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (pending.kind !== "edit" || !pending.id) return;
    setBusy(true);
    try {
      await deleteClientEntity(pending.id);
      setPending({ kind: "none" });
      setFlash("İşletme silindi");
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Silinemedi");
    } finally {
      setBusy(false);
    }
  }

  async function move(id: string, direction: -1 | 1): Promise<void> {
    if (!tree || !entities) return;
    const node = tree.byId.get(id);
    if (!node) return;
    const siblings = (
      node.row.parentId ? (tree.byId.get(node.row.parentId)?.children ?? []) : tree.roots
    ).map((n) => n.row.id);
    const idx = siblings.indexOf(id);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= siblings.length) return;
    const next = [...siblings];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    // iyimser sıralama
    setEntities(
      entities.map((e) => (next.includes(e.id) ? { ...e, orderIndex: next.indexOf(e.id) } : e)),
    );
    try {
      await reorderClientEntities(clientId, next);
    } catch (err) {
      setFlash(err instanceof Error ? err.message : "Sıralama kaydedilemedi");
      await load();
    }
  }

  /** C9 — değeri olan işletmenin altına çocuk: önce değerler yeni alt işletmeye taşınır. */
  async function moveValuesThenCreate(): Promise<void> {
    if (!moveDialog) return;
    setBusy(true);
    try {
      const parent = (entities ?? []).find((e) => e.id === moveDialog.fromId);
      const child = await createClientEntity(clientId, {
        name: draft.name.trim() || `${parent?.name ?? "İşletme"} (ana)`,
        type: "legal_entity",
        parentId: null, // önce kök olarak açılır
        country: draft.country !== "" ? draft.country : (parent?.country ?? null),
      });
      await moveEntityValues(moveDialog.fromId, child.id);
      await updateClientEntity(child.id, { parentId: moveDialog.fromId });
      setMoveDialog(null);
      setPending({ kind: "none" });
      setFlash(`${moveDialog.count} değer yeni alt işletmeye taşındı`);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Taşıma başarısız");
      setMoveDialog(null);
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <div className="p-8">
        <ErrorState message={loadError} onRetry={() => void load()} />
      </div>
    );
  }
  if (!client || !entities) {
    return (
      <div className="p-8">
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)", maxWidth: 1400 }}>
      <Link to="/clients" className="text-sm" style={{ color: "var(--color-primary)" }}>
        ← Müşteri listesine dön
      </Link>
      <h1
        className="text-3xl mt-3"
        style={{ fontFamily: "var(--font-display)", color: "var(--color-text)", fontWeight: 400 }}
      >
        {client.name}
      </h1>
      <div className="text-sm mt-1" style={{ color: "var(--color-text-2)" }}>
        {[client.sector, client.country, client.size].filter(Boolean).join(" · ") || "—"}
      </div>

      <ClientTabs
        clientId={clientId}
        active="entities"
        entityCount={entities.length}
        {...(user?.permissions["kpi.view"] === true
          ? { kpiHref: `/clients/kpi/ozet?clientId=${clientId}` }
          : {})}
      />

      <div
        className="flex gap-5 text-sm mb-3 flex-wrap items-center"
        style={{ color: "var(--color-text-2)" }}
      >
        <span>
          <b style={{ color: "var(--color-primary)", fontSize: "1.05rem" }}>{groups.length}</b> grup
        </span>
        <span>
          <b style={{ color: "var(--color-primary)", fontSize: "1.05rem" }}>{activeLeaves}</b> aktif
          işletme
        </span>
        {passiveCount > 0 ? (
          <span>
            <b style={{ color: "var(--color-text-3)", fontSize: "1.05rem" }}>{passiveCount}</b>{" "}
            pasif
          </span>
        ) : null}
        <span className="text-xs" style={{ marginLeft: "auto", color: "var(--color-text-3)" }}>
          Konsolide = tüm aktif işletmeler · Ara toplam = grup
        </span>
      </div>

      {flash ? (
        <div
          role="status"
          className="text-sm rounded-lg px-3 py-2 mb-3"
          style={{
            background: "color-mix(in srgb, var(--color-success) 12%, white)",
            color: "var(--color-success)",
          }}
        >
          {flash}
        </div>
      ) : null}

      <div
        className="grid gap-5"
        style={{ gridTemplateColumns: pending.kind === "none" ? "1fr" : "1fr 340px" }}
      >
        <div
          className="rounded-xl"
          style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
        >
          <div
            className="flex items-center gap-2 px-4 py-3"
            style={{ borderBottom: "1px solid var(--color-border)" }}
          >
            <span className="text-sm" style={{ color: "var(--color-text-2)" }}>
              {singleCompany
                ? "Tüm değerler varsayılan işletmeye bağlı. Grup şirketiyseniz grup yapısı ekleyin."
                : "Satıra tıkla → düzenle · ↑↓ sırala"}
            </span>
            <span style={{ flex: 1 }} />
            {canEdit ? (
              <>
                <button
                  type="button"
                  onClick={() => openCreate("group")}
                  className="px-3 py-1.5 rounded-lg text-sm"
                  style={{ border: "1px solid var(--color-border)", color: "var(--color-text)" }}
                >
                  {singleCompany ? "+ Grup yapısı ekle" : "+ Grup"}
                </button>
                <button
                  type="button"
                  onClick={() => openCreate("legal_entity")}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold"
                  style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
                >
                  + İşletme
                </button>
              </>
            ) : null}
          </div>
          {entities.length === 0 ? (
            <EmptyState
              title="Henüz işletme yok"
              hint="İlk değer girildiğinde varsayılan işletme otomatik açılır; grup şirketiyseniz şimdi ekleyebilirsiniz."
            />
          ) : (
            <EntityTree
              entities={entities}
              selectedId={selected?.id ?? null}
              // "Değer sayısı" kolonu: listEntities artık işletme başına valueCount döndürüyor
              valueCounts={Object.fromEntries(entities.map((e) => [e.id, e.valueCount ?? 0]))}
              canEdit={canEdit}
              onSelect={(eid) => void openEdit(eid)}
              onMove={(eid, dir) => void move(eid, dir)}
            />
          )}
          <div
            className="px-4 py-2 text-xs"
            style={{ color: "var(--color-text-3)", borderTop: "1px solid var(--color-border)" }}
          >
            Pasif işletme konsolideye girmez, yayınlanmış raporlar etkilenmez. Değeri olan işletme
            silinemez (pasife alınır). "Dahil olduğu yıllar" verilen işletme (rozet: 2023–2025)
            konsolideye yalnız o yıllarda girer.
          </div>
        </div>

        {pending.kind !== "none" ? (
          <EntityDrawer
            entity={selected}
            initialDraft={draft}
            groups={groups}
            impact={impact}
            canEdit={canEdit}
            busy={busy}
            error={formError}
            onSave={(d) => void save(d)}
            onDelete={() => void remove()}
            onCancel={() => setPending({ kind: "none" })}
          />
        ) : null}
      </div>

      <ConfirmDialog
        open={moveDialog !== null}
        title="Değerler yeni alt işletmeye taşınsın mı?"
        body={`Bu işletmenin ${moveDialog?.count ?? 0} değeri var. Alt işletme ekleyebilmek için değerler önce "${draft.name.trim() || "yeni alt işletme"}" adlı yeni alt işletmeye taşınır; üst işletme grup olur.`}
        confirmLabel="Değerleri taşı ve ekle"
        tone="neutral"
        busy={busy}
        onConfirm={() => void moveValuesThenCreate()}
        onCancel={() => setMoveDialog(null)}
      />
    </div>
  );
}
